import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  createClassApi,
  fetchClassesApi,
  fetchMyClassesApi,
  setClassPublishedApi,
  updateClassApi,
} from '~/features/catalog/api/catalog.api';

import type { DogClass, DogClassInput } from '~/features/catalog/types/catalog.type';

export interface CatalogState {
  /** 공개 클래스 — 홈·검색·상세 */
  classes: DogClass[];
  isLoading: boolean;
  isLoaded: boolean;
  /** 내가 개설한 클래스 — 지도사 관리 화면(비공개 포함). 공개 목록과 축이 달라 따로 담는다. */
  myClasses: DogClass[];
  isLoadingMyClasses: boolean;
  isSubmitting: boolean;
  currentRequestId?: string;
}

const initialState: CatalogState = {
  classes: [],
  isLoading: false,
  isLoaded: false,
  myClasses: [],
  isLoadingMyClasses: false,
  isSubmitting: false,
};

export const fetchClasses = createAsyncThunk<DogClass[], void>(
  'catalog/fetchClasses',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchClassesApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchMyClasses = createAsyncThunk<DogClass[], void>(
  'catalog/fetchMyClasses',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMyClassesApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const createClass = createAsyncThunk<DogClass, DogClassInput>(
  'catalog/createClass',
  async (input, { rejectWithValue }) => {
    try {
      return await createClassApi(input);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateClass = createAsyncThunk<DogClass, { dbId: string; input: DogClassInput }>(
  'catalog/updateClass',
  async ({ dbId, input }, { rejectWithValue }) => {
    try {
      return await updateClassApi(dbId, input);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const setClassPublished = createAsyncThunk<DogClass, { dbId: string; isPublished: boolean }>(
  'catalog/setClassPublished',
  async ({ dbId, isPublished }, { rejectWithValue }) => {
    try {
      return await setClassPublishedApi(dbId, isPublished);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/** 목록에서 방금 저장한 1건을 제자리 갱신 — 전체를 다시 불러오지 않아 스크롤이 튀지 않는다. */
function upsert(list: DogClass[], item: DogClass): DogClass[] {
  const index = list.findIndex(existing => existing.dbId === item.dbId);
  if (index === -1) return [...list, item];
  return list.map(existing => (existing.dbId === item.dbId ? item : existing));
}

const CatalogSlice = createSlice({
  initialState,
  name: 'catalog',
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchClasses.pending, (state, action) => {
        state.isLoading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.classes = action.payload;
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(fetchClasses.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.isLoading = false;
        // 실패도 "확인은 끝난" 상태로 둔다 — 무한 로딩보다 빈 목록 + 다시 불러오기가 낫다.
        state.isLoaded = true;
      })
      .addCase(fetchMyClasses.pending, state => {
        state.isLoadingMyClasses = true;
      })
      .addCase(fetchMyClasses.fulfilled, (state, action) => {
        state.myClasses = action.payload;
        state.isLoadingMyClasses = false;
      })
      .addCase(fetchMyClasses.rejected, state => {
        state.isLoadingMyClasses = false;
      })
      .addCase(createClass.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(createClass.fulfilled, (state, action) => {
        state.myClasses = upsert(state.myClasses, action.payload);
        state.isSubmitting = false;
      })
      .addCase(createClass.rejected, state => {
        state.isSubmitting = false;
      })
      .addCase(updateClass.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(updateClass.fulfilled, (state, action) => {
        state.myClasses = upsert(state.myClasses, action.payload);
        state.isSubmitting = false;
      })
      .addCase(updateClass.rejected, state => {
        state.isSubmitting = false;
      })
      .addCase(setClassPublished.fulfilled, (state, action) => {
        state.myClasses = upsert(state.myClasses, action.payload);
      });
  },
});

export default CatalogSlice;
